/* eslint-disable no-console */
import { register } from 'register-service-worker';

import { startBuildVersionGuard } from '@/utils/buildVersionGuard';
import { reloadWhenIdle } from '@/utils/deferredReload';

// Commit hash is injected at build time via VUE_APP_COMMIT_HASH (see Dockerfile).
// Workbox embeds it into the precache manifest so any new deploy produces a
// byte-different service-worker.js, triggering the browser's update algorithm.
const SW_VERSION = process.env.VUE_APP_COMMIT_HASH || 'local';
const LOCAL_HOST_PATTERN = /(^localhost$|^127\.0\.0\.1$|\.profiteia\.local$)/;
const isLocalEnvironment = process.env.VUE_APP_ENVIRONMENT === 'local';
const isE2EEnvironment = process.env.VUE_APP_E2E === 'true';
const isLocalHost =
  typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname);
const hashSearch =
  typeof window !== 'undefined' && window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : '';
const hasNoServiceWorkerParam =
  typeof window !== 'undefined' &&
  (new URLSearchParams(window.location.search).get('noServiceWorker') === 'true' ||
    new URLSearchParams(hashSearch).get('noServiceWorker') === 'true');
// Set by the `noServiceWorker` bootstrap in public/index.html before it strips
// the param from the URL (that strip is a same-document fragment navigation, so
// this module still evaluates in the same document — see the comment there).
// Session-scoped: it dies with the tab, which is exactly the flag's intent.
const hasStoredNoServiceWorkerFlag = (() => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('profiteia:no-service-worker') === 'true'
    );
  } catch {
    return false;
  }
})();
const shouldDisableServiceWorker =
  isLocalEnvironment ||
  isLocalHost ||
  isE2EEnvironment ||
  hasNoServiceWorkerParam ||
  hasStoredNoServiceWorkerFlag;

function unregisterLocalServiceWorkers() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister()))
    )
    .then(() => {
      if (typeof window === 'undefined' || !('caches' in window)) return undefined;
      return caches.keys().then((keys) => Promise.all(keys.map((key) => caches.delete(key))));
    })
    .then(() => {
      console.log(`[SW ${SW_VERSION}] Disabled for local development hosts.`);
    })
    .catch((error) => {
      console.error(`[SW ${SW_VERSION}] Local unregister failed:`, error);
    });
}

if (process.env.NODE_ENV !== 'development' && shouldDisableServiceWorker) {
  unregisterLocalServiceWorkers();
} else if (process.env.NODE_ENV !== 'development' && 'serviceWorker' in navigator) {
  // When a new SW activates it immediately claims all clients (clientsClaim: true
  // in vue.config.js workboxOptions) and skips waiting (skipWaiting: true).
  // We listen for the resulting controllerchange event and reload so every open
  // tab instantly switches to the freshly cached assets without any user prompt.
  // Network-level safety net for the silent CSS/asset skew that the SW's own
  // controllerchange reload can miss: if the running build no longer matches the
  // freshly-served index.html, reload once to realign. See buildVersionGuard.js.
  startBuildVersionGuard();

  let reloadPending = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!reloadPending) {
      reloadPending = true;
      // Defer: never reload mid-interaction (aborts an in-flight login on
      // mobile → generic "network error"). Applies when the tab next hides.
      console.log(`[SW ${SW_VERSION}] New version active — reloading when idle.`);
      reloadWhenIdle();
    }
  });

  register('./service-worker.js', {
    ready() {
      console.log(`[SW ${SW_VERSION}] Serving from cache.`);
    },
    registered(registration) {
      console.log(`[SW ${SW_VERSION}] Registered.`);

      // Actively poll for updates so long-lived tabs pick up new deploys quickly.
      const checkForUpdates = () => registration.update().catch(console.error);
      checkForUpdates();
      setInterval(checkForUpdates, 60 * 60 * 1000); // every hour
      // Only check when the tab returns to the foreground / reconnects. We do
      // NOT listen on `focus`/`pageshow`: on mobile those fire throughout the
      // login ceremony (keyboard, autofill, biometric sheet) and drove a reload
      // storm that aborted the sign-in request. `visibilitychange` covers the
      // real "user came back after a deploy" case without that churn.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdates();
      });
      window.addEventListener('online', checkForUpdates);
    },
    cached() {
      console.log(`[SW ${SW_VERSION}] Assets cached for offline use.`);
    },
    updatefound() {
      console.log(`[SW ${SW_VERSION}] New version found — downloading.`);
    },
    updated() {
      // skipWaiting fires automatically (workbox config), so controllerchange
      // will follow shortly and trigger the reload above.
      console.log(`[SW ${SW_VERSION}] New version installed.`);
    },
    offline() {},
    error(error) {
      console.error(`[SW ${SW_VERSION}] Registration error:`, error);
    },
  });
} else if (process.env.NODE_ENV !== 'development') {
  console.log(`[SW ${SW_VERSION}] Service workers are not available on this origin.`);
}
